import "./ProductsPage.scss";

import { Col, Row } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import {
  faAdd,
  faBoxOpen,
  faBoxesStacked,
  faChartLine,
  faDollarSign,
  faMoneyBillTrendUp,
  faTriangleExclamation,
  faWallet,
} from "@fortawesome/free-solid-svg-icons";

import AppLoading from "../../commons/AppLoading";
import CompanyAPI from "../company/CompanyAPI";
import ConfirmModal from "../../commons/modal/ConfirmModal";
import EmptyState from "../../commons/EmptyState";
import FloatingButton from "../../commons/buttons/FloatingButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HallAppToast from "../../commons/HallAppToast";
import MenuTitle from "../../commons/menu/MenuTitle";
import ModalProduct from "./ModalProducts";
import ModalProductStock from "./components/ModalProductStock";
import ModalSellProduct from "./components/ModalSellProduct";
import ProductCard from "./components/ProductCard";
import ProductReportAPI from "./ProductReportAPI";
import ProductsAPI from "./ProductsAPI";
import { useNavigate } from "react-router-dom";

const productsAPI = new ProductsAPI();
const companyAPI = new CompanyAPI();
const productReport = new ProductReportAPI();

const EMPTY_SUMMARY = {
  totalInvestedCost: 0,
  currentStockCostValue: 0,
  totalRevenueCustomer: 0,
  totalRevenueEmployee: 0,
  totalRevenue: 0,
  totalRealizedProfitCustomer: 0,
  totalRealizedProfitEmployee: 0,
  totalRealizedProfit: 0,
  potentialRevenueCustomer: 0,
  potentialRevenueEmployee: 0,
  potentialProfitCustomer: 0,
  potentialProfitEmployee: 0,
  totalCommission: 0,
  totalLossValue: 0,
  totalConsumptionValue: 0,
};

function toMoney(value) {
  const number = Number(value || 0);

  return number.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export default function ProductsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState([]);

  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);

  const [showModal, setShowModal] = useState(false);
  const [productToEdit, setProductToEdit] = useState(null);

  const [showStockModal, setShowStockModal] = useState(false);
  const [productToManageStock, setProductToManageStock] = useState(null);

  const [showModalDelete, setShowModalDelete] = useState(false);
  const [isLoadingModalDelete, setIsLoadingModalDelete] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  const [showSellModal, setShowSellModal] = useState(false);
  const [productToSell, setProductToSell] = useState(null);

  const [employees, setEmployees] = useState([]);

  const navigate = useNavigate();

  const openSell = (product) => {
    setProductToSell(product);
    setShowSellModal(true);
  };

  const closeSell = () => {
    setShowSellModal(false);
    setProductToSell(null);
  };

  const loadEmployees = async () => {
    try {
      const response = await companyAPI.getAllEmployee(
        companyAPI.getCompanyManagment().id
      );

      const options = (response.data || []).map((d) => ({
        value: d.id,
        label: d.name,
      }));

      setEmployees(options);
    } catch {
      setEmployees([]);
    }
  };

  const loadProducts = async () => {
    try {
      setIsLoading(true);

      const resp = await productsAPI.findAll();
      const data = resp?.data || resp;
      const list = data?.items || data;

      setItems(Array.isArray(list) ? list : []);
    } catch (e) {
      setItems([]);
      HallAppToast.genericError();
    } finally {
      setIsLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      setIsLoadingSummary(true);

      const response = await productReport.getSummary(
        companyAPI.getCompanyManagment().id
      );

      setSummary(response?.data || response || EMPTY_SUMMARY);
    } catch (e) {
      setSummary(EMPTY_SUMMARY);
    } finally {
      setIsLoadingSummary(false);
    }
  };

  const reloadPageData = async () => {
    await Promise.all([loadProducts(), loadSummary()]);
  };

  useEffect(() => {
    loadProducts();
    loadSummary();
    loadEmployees();
  }, []);

  const openCreate = () => {
    setProductToEdit(null);
    setShowModal(true);
  };

  const openEdit = (product) => {
    setProductToEdit(product);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setProductToEdit(null);
  };

  const toggleStatus = async (product) => {
    try {
      setIsLoading(true);

      await productsAPI.update(product.id, {
        ...product,
        active: !product?.active,
      });

      HallAppToast.successWithMessage("Status atualizado com sucesso.");
      await loadProducts();
    } catch (e) {
      HallAppToast.genericError();
      setIsLoading(false);
    }
  };

  const openDeleteModal = (product) => {
    setCurrentItem(product);
    setShowModalDelete(true);
  };

  const closeDeleteModal = () => {
    if (isLoadingModalDelete) return;
    setShowModalDelete(false);
    setCurrentItem(null);
  };

  const executeDeletion = async () => {
    if (!currentItem?.id) return;

    setIsLoadingModalDelete(true);

    try {
      await productsAPI.delete(currentItem.id);

      HallAppToast.success("Produto excluído com sucesso.");
      setShowModalDelete(false);
      setCurrentItem(null);

      await reloadPageData();
    } catch (e) {
      HallAppToast.genericError();
    } finally {
      setIsLoadingModalDelete(false);
    }
  };

  const handleManageStock = (product) => {
    setProductToManageStock(product);
    setProductToEdit(product);
    setShowStockModal(true);
  };

  const closeStockModal = () => {
    setShowStockModal(false);
    setProductToManageStock(null);
  };

  const handleStockSaved = async () => {
    await reloadPageData();
    closeStockModal();
  };

  const summaryCards = [
    {
      key: "totalInvestedCost",
      title: "Total investido",
      value: toMoney(summary.totalInvestedCost),
      icon: faWallet,
      helper: "Quanto já foi investido em compras",
    },
    {
      key: "currentStockCostValue",
      title: "Valor em estoque",
      value: toMoney(summary.currentStockCostValue),
      icon: faBoxesStacked,
      helper: "Custo estimado do estoque atual",
    },
    {
      key: "totalRevenue",
      title: "Faturamento",
      value: toMoney(summary.totalRevenue),
      icon: faDollarSign,
      helper: "Total vendido em produtos",
    },
    {
      key: "totalRealizedProfit",
      title: "Lucro realizado",
      value: toMoney(summary.totalRealizedProfit),
      icon: faChartLine,
      helper: "Lucro das vendas já concluídas",
    },
    {
      key: "potentialProfitCustomer",
      title: "Lucro potencial cliente",
      value: toMoney(summary.potentialProfitCustomer),
      icon: faMoneyBillTrendUp,
      helper: "Se vender o estoque atual para cliente final",
    },
    {
      key: "totalLossValue",
      title: "Perdas",
      value: toMoney(summary.totalLossValue),
      icon: faTriangleExclamation,
      helper: "Perdas e avarias registradas",
    },
  ];

  return (
    <>
      <div className="container products-page">
        <MenuTitle
          title="Produtos"
          subtitle="Cadastre e gerencie os produtos da sua empresa."
          showArrowInfo={false}
        />

        <div className="products-page__tabs">
          <button type="button" className="products-page__tab-button active">
            Produtos
          </button>

          <button
            type="button"
            className="products-page__tab-button"
            onClick={() => navigate("/produtos/comissoes")}
          >
            Comissões
          </button>
        </div>

        <section className="products-page__summary">
          <div className="products-page__summary-header">
            <div>
              <h3>Resumo de estoque e vendas</h3>
              <p>Indicadores gerais dos produtos da empresa.</p>
            </div>
          </div>

          {isLoadingSummary ? (
            <div className="products-page__summary-loading">
              Carregando resumo...
            </div>
          ) : (
            <>
              <div className="products-page__summary-grid">
                {summaryCards.map((card) => (
                  <div className="products-page__summary-card" key={card.key}>
                    <div className="products-page__summary-card-icon">
                      <FontAwesomeIcon icon={card.icon} />
                    </div>

                    <div className="products-page__summary-card-content">
                      <span className="title">{card.title}</span>
                      <strong className="value">{card.value}</strong>
                      <small className="helper">{card.helper}</small>
                    </div>
                  </div>
                ))}
              </div>

              <div className="products-page__summary-details">
                <div className="products-page__summary-box">
                  <h4>Faturamento por tipo</h4>

                  <div className="products-page__summary-row">
                    <span>Cliente final</span>
                    <strong>{toMoney(summary.totalRevenueCustomer)}</strong>
                  </div>

                  <div className="products-page__summary-row">
                    <span>Profissional</span>
                    <strong>{toMoney(summary.totalRevenueEmployee)}</strong>
                  </div>
                </div>

                <div className="products-page__summary-box">
                  <h4>Lucro por tipo</h4>

                  <div className="products-page__summary-row">
                    <span>Cliente final</span>
                    <strong>{toMoney(summary.totalRealizedProfitCustomer)}</strong>
                  </div>

                  <div className="products-page__summary-row">
                    <span>Profissional</span>
                    <strong>{toMoney(summary.totalRealizedProfitEmployee)}</strong>
                  </div>
                </div>

                <div className="products-page__summary-box">
                  <h4>Potencial do estoque</h4>

                  <div className="products-page__summary-row">
                    <span>Receita potencial cliente</span>
                    <strong>{toMoney(summary.potentialRevenueCustomer)}</strong>
                  </div>

                  <div className="products-page__summary-row">
                    <span>Receita potencial profissional</span>
                    <strong>{toMoney(summary.potentialRevenueEmployee)}</strong>
                  </div>

                  <div className="products-page__summary-row">
                    <span>Lucro potencial profissional</span>
                    <strong>{toMoney(summary.potentialProfitEmployee)}</strong>
                  </div>
                </div>

                <div className="products-page__summary-box">
                  <h4>Outros indicadores</h4>

                  <div className="products-page__summary-row">
                    <span>Comissões</span>
                    <strong>{toMoney(summary.totalCommission)}</strong>
                  </div>

                  <div className="products-page__summary-row">
                    <span>Consumo interno</span>
                    <strong>{toMoney(summary.totalConsumptionValue)}</strong>
                  </div>
                </div>
              </div>
            </>
          )}
        </section>

        {isLoading ? (
          <AppLoading />
        ) : (
          <Row className="g-3" style={{ margin: 0, width: "100%" }}>
            {items.length === 0 ? (
              <EmptyState
                title="Nenhum produto criado"
                description="Cadastre produtos para organizar melhor vendas, consumo e controle interno."
                icon={<FontAwesomeIcon icon={faBoxOpen} />}
                actionLabel="Criar meu primeiro produto"
                onAction={openCreate}
              />
            ) : (
              items.map((product) => (
                <Col xs={12} sm={6} md={4} lg={4} key={product.id} className="mb-3">
                  <ProductCard
                    product={product}
                    onEdit={() => openEdit(product)}
                    onToggleStatus={() => toggleStatus(product)}
                    onDelete={() => openDeleteModal(product)}
                    onManageStock={() => handleManageStock(product)}
                    onSell={() => openSell(product)}
                    onOpenHistory={() =>
                      navigate(`/produtos/${product.id}/movimentos`)
                    }
                  />
                </Col>
              ))
            )}

            <div style={{ height: "calc(50px + 72px)" }} />
          </Row>
        )}
      </div>

      <FloatingButton icon={faAdd} onClick={openCreate} />

      {showModal && (
        <ModalProduct
          show={showModal}
          closeShow={closeModal}
          productToEdit={productToEdit}
          callGetProducts={reloadPageData}
        />
      )}

      {showStockModal && (
        <ModalProductStock
          show={showStockModal}
          closeShow={closeStockModal}
          productToEdit={productToEdit}
          product={productToManageStock}
          onSuccess={handleStockSaved}
        />
      )}

      {showModalDelete && (
        <ConfirmModal
          show={showModalDelete}
          title="Excluir produto"
          isLoading={isLoadingModalDelete}
          confirm={executeDeletion}
          closeModal={closeDeleteModal}
        >
          Deseja realmente remover o produto{" "}
          <strong>{currentItem?.name || ""}</strong>?
        </ConfirmModal>
      )}

      {showSellModal && productToSell && (
        <ModalSellProduct
          show={showSellModal}
          closeShow={closeSell}
          product={productToSell}
          employees={employees}
          onSuccess={reloadPageData}
        />
      )}
    </>
  );
}